import React, { useState, useMemo } from 'react';
import { calculate } from '../lib/calculations';
import NumberStepper from './NumberStepper';

/**
 * BreakEvenBar — standalone bar that lives directly under
 * ProfitPriceFinderBar. Same dark-header collapsible pattern for visual
 * harmony, but accented purple ("PLANNING") so the two bars feel like
 * siblings, not duplicates.
 *
 * Inside: one fixed-costs input that auto-computes the units needed to
 * cover those costs at the current product's per-sale profit. Plus a
 * "common levels" reference strip so users can see the shape of the
 * relationship without typing.
 */

const COMMON_FIXED_LEVELS = [50, 100, 250, 500, 1000];

export default function BreakEvenBar({ inputs, settings, currencySymbol = '$', resetSignal }) {
  const [open, setOpen] = useState(false);
  const [fixedCosts, setFixedCosts] = useState('');

  // Reset on resetSignal change
  React.useEffect(() => {
    setFixedCosts('');
    setOpen(false);
  }, [resetSignal]);

  // Profit per item (q=1, separate-order, taxes respected when enabled).
  // Mirrors how the old break-even block computed it inside ForecastPanel.
  const perSale = useMemo(() => {
    if (!inputs?.price) return null;
    const r = calculate(
      { ...inputs, quantity: '1' },
      { ...settings, orderType: 'separate' }
    );
    if (!r || r.revenue <= 0) return null;
    const hasTax = (settings?.taxRate || 0) > 0;
    const headlineProfit = hasTax ? (r.afterTaxProfit ?? r.profit) : r.profit;
    return {
      profit: r.profit,
      afterTax: r.afterTaxProfit ?? r.profit,
      headline: headlineProfit,
      hasTax,
      taxRate: settings?.taxRate || 0,
      sym: r.currencySymbol || currencySymbol,
    };
  }, [inputs, settings, currencySymbol]);

  const sym = perSale?.sym || currencySymbol;

  const fc = parseFloat(fixedCosts) || 0;
  const unitsNeeded = (perSale && perSale.headline > 0 && fc > 0)
    ? Math.ceil(fc / perSale.headline)
    : null;

  // Don't render if no calculation has been done yet
  if (!perSale) return null;

  const ACCENT = '#7e22ce';      // purple
  const ACCENT_DEEP = '#581c87';
  const ACCENT_BG = '#f3e8ff';
  const ACCENT_TAG = '#a21caf';  // tag chip background

  return (
    <div
      style={{
        background: '#fefdfa',
        border: '2px solid #1a1a2e',
        borderRadius: '1rem',
        overflow: 'hidden',
        boxShadow:
          '0 2px 0 rgba(26,26,46,0.08), 0 6px 16px rgba(26,26,46,0.06)',
      }}
    >
      {/* ─── Dark header ─── */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.85rem',
          width: '100%',
          padding: '0.85rem 1.1rem',
          background: 'linear-gradient(180deg, #1e293b 0%, #1a1a2e 100%)',
          color: '#fefdf8',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          transition: 'background 0.15s',
          fontFamily: 'Inter, sans-serif',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, #243144 0%, #20233a 100%)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, #1e293b 0%, #1a1a2e 100%)'; }}
      >
        {/* Icon medallion — purple-tinted to differentiate from finder bar */}
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '38px',
            height: '38px',
            borderRadius: '50%',
            background: 'rgba(168, 85, 247, 0.18)',
            border: '1.5px solid rgba(168, 85, 247, 0.4)',
            fontSize: '1.15rem',
            flexShrink: 0,
          }}
        >
          ⚖️
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.02rem', fontWeight: 700, letterSpacing: '-0.005em' }}>
              Plan your break-even
            </span>
            <span
              style={{
                fontSize: '0.62rem',
                fontWeight: 800,
                letterSpacing: '0.12em',
                padding: '0.12rem 0.45rem',
                background: ACCENT_TAG,
                color: '#fefdf8',
                borderRadius: '0.3rem',
                lineHeight: 1.2,
              }}
            >
              PLANNING
            </span>
          </div>
          <p style={{ fontSize: '0.82rem', color: 'rgba(254, 253, 248, 0.75)', margin: '2px 0 0', lineHeight: 1.35 }}>
            How many sales/month would cover your fixed costs (Etsy Plus, ads, supplies)?
          </p>
        </div>

        <span
          aria-hidden="true"
          style={{
            fontSize: '0.95rem',
            transition: 'transform 0.18s',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            opacity: 0.85,
            flexShrink: 0,
          }}
        >
          ▶
        </span>
      </button>

      {/* ─── Expanded body ─── */}
      {open && (
        <div style={{ padding: '1rem 1.1rem 1.1rem' }}>
          {/* Step 1: Fixed costs input */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.85rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 auto', minWidth: '160px', maxWidth: '200px' }}>
              <label
                style={{
                  display: 'block',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: 'rgba(26,26,46,0.75)',
                  marginBottom: '4px',
                }}
              >
                Your monthly fixed costs ({sym})
              </label>
              <NumberStepper
                value={fixedCosts}
                onChange={setFixedCosts}
                step={5}
                min={0}
                placeholder="e.g. 50"
                inputClassName="sketch-input"
              />
            </div>
            <p
              className="font-body text-xs text-ink/72"
              style={{ flex: 1, minWidth: '200px', lineHeight: 1.4, marginBottom: '0.45rem' }}
            >
              <strong style={{ color: 'rgba(26,26,46,0.85)' }}>What counts:</strong> Etsy Plus
              ($10/mo), Etsy Ads, recurring software, supply replenishment, booth rent — anything
              you'd pay regardless of whether you made a sale this month.
            </p>
          </div>

          {/* Step 2: Result panel — only when we have both inputs */}
          {perSale.headline <= 0 ? (
            <div
              style={{
                marginTop: '0.95rem',
                padding: '0.7rem 0.85rem',
                background: '#fee2e2',
                border: '1.5px solid #9f1239',
                borderRadius: '0.55rem',
                color: '#7f1d1d',
                fontSize: '0.88rem',
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              ⚠️ Each sale earns {sym}{perSale.headline.toFixed(2)} of profit — you can't break
              even on any fixed cost at this price. Use the Find Target Price bar above to fix it.
            </div>
          ) : fc > 0 ? (
            <div
              style={{
                marginTop: '0.95rem',
                padding: '0.95rem 1.05rem',
                background: ACCENT_BG,
                border: `1.5px solid ${ACCENT}`,
                borderRadius: '0.6rem',
                textAlign: 'center',
              }}
            >
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  color: ACCENT,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  margin: '0 0 0.4rem',
                }}
              >
                Your break-even
              </p>
              <p
                className="font-numbers"
                style={{
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: ACCENT_DEEP,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.05,
                  margin: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {unitsNeeded.toLocaleString('en-US')}
                <span style={{ fontSize: '1.05rem', fontWeight: 600, color: 'rgba(26,26,46,0.7)', marginLeft: '0.3rem' }}>
                  sales / month
                </span>
              </p>
              <p
                className="font-body text-sm text-ink/72"
                style={{ marginTop: '0.35rem', lineHeight: 1.4 }}
              >
                to clear <strong style={{ color: '#1a1a2e' }}>{sym}{fc.toLocaleString('en-US')}/mo</strong> in fixed costs at{' '}
                <strong style={{ color: '#047857' }}>{sym}{perSale.headline.toFixed(2)}</strong> profit per item
                {perSale.hasTax ? ` (after ${perSale.taxRate}% tax)` : ''}.
              </p>
            </div>
          ) : null}

          {/* Step 3: Common levels reference — always shown when math works */}
          {perSale.headline > 0 && (
            <div style={{ marginTop: '0.95rem' }}>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  color: 'rgba(26,26,46,0.55)',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  marginBottom: '0.5rem',
                }}
              >
                Quick reference — sales/mo at common cost levels
              </p>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {COMMON_FIXED_LEVELS.map(level => {
                  const units = Math.ceil(level / perSale.headline);
                  const isCurrent = fc > 0 && Math.abs(level - fc) < 0.01;
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setFixedCosts(String(level))}
                      title={`Set fixed costs to ${sym}${level}/mo`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: '0.32rem',
                        padding: '0.32rem 0.7rem',
                        background: isCurrent ? `${ACCENT}15` : '#fefdf8',
                        border: `1.5px solid ${isCurrent ? ACCENT : 'rgba(26,26,46,0.15)'}`,
                        borderRadius: '0.5rem',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.82rem',
                        cursor: 'pointer',
                        transition: 'all 0.12s',
                        color: '#1a1a2e',
                      }}
                    >
                      <span style={{ fontWeight: 600, color: 'rgba(26,26,46,0.7)' }}>
                        {sym}{level}/mo
                      </span>
                      <span style={{ color: 'rgba(26,26,46,0.45)', fontSize: '0.72rem' }}>→</span>
                      <span
                        className="font-numbers"
                        style={{ fontWeight: 700, color: ACCENT, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {units}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: 'rgba(26,26,46,0.55)' }}>
                        sales
                      </span>
                    </button>
                  );
                })}
              </div>
              <p
                className="font-note text-note text-ink/68"
                style={{ marginTop: '0.5rem' }}
              >
                ~ click any level to load it into the input above ~
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
